import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Add from '@mui/icons-material/Add';
import { Tooltip } from '@ublend-npm/aulaui-next';
import MaterialListItem from '../MaterialList/MaterialListItem.container';
import MaterialMenu from '../MaterialList/MaterialMenu.container';
import RenameFolderDialog from '../MaterialList/RenameFolderDialog.container';
import DeleteFolderDialog from '../MaterialList/DeleteFolderDialog.container';
import EmptyMaterialList from '../MaterialList/EmptyMaterialList';
import {
  AddButton,
  MaterialListTitle,
  MaterialListWrapper,
  MaterialListScrollWrapper,
  MaterialListItemsWrapper,
} from './MaterialListEducatorOnly.styled';
import ScrollBar from '../../../common/ScrollBar';
import RestoreMaterialDrawer from '../MaterialList/RestoreMaterialDrawer.container';
import { MATERIAL_PERMISSIONS } from '@core/materials/permissions';

const MaterialListEducatorOnly = ({
  classRoomId,
  onAddClick,
  onPermissionDenied,
  materialEducatorOnlyIds,
  isFetchingSections,
  isReadOnlyUser,
  canView = true,
  canCreate = false,
  permissions = [],
  isRbacEnabled = false,
  isStudent = false,
  onCreateFolder,
  onImportMaterial,
  onExportMaterial,
}) => {
  const [restoring, setRestoring] = useState(false);

  if (!canView) {
    return null;
  }

  // In educator area: if user has educator area permission, they can do everything
  // No need to check individual permissions - canCreate is already canAccessEducatorArea
  const canPerformActions = canCreate;

  const handleAddClick = () => {
    if (!canPerformActions) {
      onPermissionDenied(MATERIAL_PERMISSIONS.EDUCATOR_AREA);
      return;
    }
    onAddClick();
  };

  // For students: only show "+" button if they have educator area permission
  // For educators: show button (disabled with tooltip if no permission)
  const shouldShowAddButton = isStudent
    ? isRbacEnabled && canPerformActions
    : true;

  return (
    <MaterialListWrapper>
      <ScrollBar autoHide>
        <MaterialListScrollWrapper>
          <MaterialListTitle
            component="div"
            title="Materials"
            testID="menu-materials"
          >
            {!isReadOnlyUser && (
              <>
                <MaterialMenu
                  educatorOnly
                  onRestorePage={() => setRestoring(true)}
                  isStudent={isStudent}
                  canCreateFolder={canPerformActions}
                  canImportMaterial={canPerformActions}
                  canExportMaterial={canPerformActions}
                  permissions={permissions}
                  isRbacEnabled={isRbacEnabled}
                  onCreateFolder={onCreateFolder}
                  onImportMaterial={onImportMaterial}
                  onExportMaterial={onExportMaterial}
                />
                <RestoreMaterialDrawer
                  classRoomId={classRoomId}
                  educatorArea
                  open={restoring}
                  onClose={() => setRestoring(false)}
                />
                {shouldShowAddButton && (
                  <Tooltip
                    title={
                      canPerformActions
                        ? 'New page'
                        : 'You do not have permission to create pages'
                    }
                    space={4}
                    position="right"
                  >
                    <div>
                      <AddButton
                        type="secondary"
                        size="xSmall"
                        icon={Add}
                        label="Create page"
                        onClick={handleAddClick}
                        disabled={!canPerformActions}
                      />
                    </div>
                  </Tooltip>
                )}
              </>
            )}
          </MaterialListTitle>
          <MaterialListItemsWrapper>
            {materialEducatorOnlyIds.length || isFetchingSections ? (
              materialEducatorOnlyIds.map((id, index) => (
                <MaterialListItem
                  key={id}
                  materialId={id}
                  educatorOnly
                  data-testid={`educator-material-${index + 1}`}
                />
              ))
            ) : (
              <EmptyMaterialList isInstructor={canPerformActions} />
            )}
          </MaterialListItemsWrapper>

          <RenameFolderDialog educatorOnly />
          <DeleteFolderDialog educatorOnly />
        </MaterialListScrollWrapper>
      </ScrollBar>
    </MaterialListWrapper>
  );
};

MaterialListEducatorOnly.propTypes = {
  classRoomId: PropTypes.string.isRequired,
  onAddClick: PropTypes.func.isRequired,
  onPermissionDenied: PropTypes.func,
  materialEducatorOnlyIds: PropTypes.arrayOf(PropTypes.string),
  isFetchingSections: PropTypes.bool.isRequired,
  isReadOnlyUser: PropTypes.bool,
  canView: PropTypes.bool,
  canCreate: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  isStudent: PropTypes.bool,
  onCreateFolder: PropTypes.func,
  onImportMaterial: PropTypes.func,
  onExportMaterial: PropTypes.func,
};

MaterialListEducatorOnly.defaultProps = {
  materialEducatorOnlyIds: [],
  onPermissionDenied: () => {},
  isReadOnlyUser: false,
  canView: true,
  canCreate: false,
  permissions: [],
  isRbacEnabled: false,
  isStudent: false,
};

export default React.memo(MaterialListEducatorOnly);
